import React, { useState, useMemo, useCallback } from 'react';
import { Plus, Upload, Lightbulb, X, Check } from 'lucide-react';
import {
  SectionHeading, FilterSearch, FilterToggle,
  Button, EmptyState, NNSelect, ICON_PX,
  Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle,
} from '../ui';
import { usePracticeManagement } from '../../contexts/PracticeManagementContext';
import TaskCard from '../shared/TaskCard';
import TaskEditorPanel from '../shared/TaskEditorPanel';

const STATUS_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

const DOMAIN_OPTIONS = [
  { value: 'quality', label: 'Quality' }, { value: 'staffing', label: 'Staffing' },
  { value: 'access', label: 'Access' }, { value: 'finance', label: 'Finance' },
  { value: 'compliance', label: 'Compliance' }, { value: 'safety', label: 'Safety' },
  { value: 'operations', label: 'Operations' }, { value: 'communications', label: 'Comms' },
];

const CADENCE_OPTIONS = [
  { value: 'daily', label: 'Daily' }, { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Biweekly' }, { value: 'monthly', label: 'Monthly' },
];

const COMPLETION_OPTIONS = [
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'checkbox_note', label: 'Checkbox + Note' },
  { value: 'checkbox_linked', label: 'Checkbox + Linked' },
];

const TIER_OPTIONS = [
  { value: 'A', label: 'Tier A — Same-day' }, { value: 'B', label: 'Tier B — 24h' },
  { value: 'C', label: 'Tier C — 72h' }, { value: 'D', label: 'Tier D — Flexible' },
];

const ROLE_OPTIONS = [
  { value: 'com', label: 'COM' }, { value: 'triage_rn', label: 'Triage RN' },
  { value: 'charge_nurse', label: 'Charge Nurse' }, { value: 'front_desk', label: 'Front Desk' },
  { value: 'scheduling_coord', label: 'Scheduling Coord' },
];

function PMTaskLibraryTab({ readOnly = false }) {
  const { taskLibrary, toggleTaskActive, updateTask, addCustomTask, restoreTask } = usePracticeManagement();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedTask, setSelectedTask] = useState(null);
  const [showGuide, setShowGuide] = useState(() => !localStorage.getItem('pm-activation-dismissed'));
  const [addOpen, setAddOpen] = useState(false);
  const [newTask, setNewTask] = useState(null);

  const filtered = useMemo(() => {
    let result = taskLibrary;
    // Multi-filter search with + separator
    result = FilterSearch.filter(result, search, (t) => [
      t.name, t.domain, t.cadence, t.completionType?.replace(/_/g, ' '),
      t.description, t.defaultOwnerRole?.replace(/_/g, ' '),
    ]);
    if (statusFilter === 'active') result = result.filter((t) => t.active);
    if (statusFilter === 'inactive') result = result.filter((t) => !t.active);
    return result;
  }, [taskLibrary, search, statusFilter]);

  // Group by domain
  const grouped = useMemo(() => {
    const map = {};
    filtered.forEach((t) => {
      if (!map[t.domain]) map[t.domain] = [];
      map[t.domain].push(t);
    });
    return Object.entries(map).sort(([a], [b]) => a.localeCompare(b));
  }, [filtered]);

  const handleTaskClick = useCallback((task) => {
    setSelectedTask(task);
  }, []);

  const handleSave = useCallback((updatedTask) => {
    updateTask(updatedTask.id, updatedTask);
    setSelectedTask((prev) => prev?.id === updatedTask.id ? { ...prev, ...updatedTask } : prev);
  }, [updateTask]);

  // ── Add Task dialog ──
  const openAddDialog = useCallback(() => {
    setNewTask({
      name: '',
      domain: 'operations',
      cadence: 'daily',
      estimatedMinutes: 15,
      completionType: 'checkbox',
      defaultOwnerRole: 'com',
      criticalityTier: 'C',
      regulatorySource: null,
      description: '',
      toolReference: '',
    });
    setAddOpen(true);
  }, []);

  const handleAddConfirm = useCallback(() => {
    if (!newTask?.name?.trim()) return;
    addCustomTask(newTask);
    setAddOpen(false);
    // Auto-select the new task (it will be the last in the library)
    setTimeout(() => {
      const added = taskLibrary.find((t) => t.name === newTask.name);
      if (added) setSelectedTask(added);
    }, 100);
  }, [newTask, addCustomTask, taskLibrary]);

  const updateNewField = useCallback((field, value) => {
    setNewTask((prev) => ({ ...prev, [field]: value }));
  }, []);

  const activeCount = taskLibrary.filter((t) => t.active).length;
  const totalCount = taskLibrary.length;

  return (
    <div className="flex flex-col h-full py-3">
      {/* Header */}
      <SectionHeading
        size="sm"
        title="Task Library"
        className="mb-1.5 shrink-0"
        info="Manage your practice's operational task library. Toggle tasks on/off, edit details, or create custom tasks."
        aiContext="pm.task-library"
        action={
          <div className="flex items-center gap-2">
            <span className="text-body-sm text-text-muted">{activeCount}/{totalCount} active</span>
            {!readOnly && (
              <>
                <Button variant="ghost" size="sm">
                  <Upload size={ICON_PX.sm} className="mr-1" /> Import
                </Button>
                <Button variant="create" size="sm" onClick={openAddDialog}>
                  <Plus size={ICON_PX.md} className="mr-1" /> Add Task
                </Button>
              </>
            )}
          </div>
        }
      />

      {/* First-run activation guide */}
      {showGuide && (
        <div className="rounded-lg border border-aqua bg-aqua-glow/10 p-3 flex items-start gap-3 shrink-0 mb-2">
          <Lightbulb size={ICON_PX.lg} className="text-aqua shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-body-sm font-medium text-text-dark">Welcome to your Task Library</p>
            <p className="text-body-sm text-text-secondary mt-0.5">
              These {totalCount} tasks are pre-loaded for your practice. Review by domain, toggle off what
              doesn't apply, and edit tool references to match your systems. You're ready to go.
            </p>
          </div>
          <button
            type="button"
            onClick={() => { setShowGuide(false); localStorage.setItem('pm-activation-dismissed', '1'); }}
            className="text-text-muted hover:text-text-dark shrink-0"
          >
            <X size={ICON_PX.md} />
          </button>
        </div>
      )}

      {/* Left-Right Split */}
      <div className="flex flex-1 min-h-0 gap-2">
        {/* LEFT: Task Catalog */}
        <div className="flex flex-col w-2/5 min-w-[320px] shrink-0">
          {/* Search + Status toggle */}
          <div className="shrink-0 mb-2 flex items-center gap-2">
            <FilterSearch value={search} onChange={setSearch} />
            <FilterToggle value={statusFilter} onChange={setStatusFilter} options={STATUS_FILTERS} />
          </div>

          {/* Task list (scrollable) */}
          <div className="flex-1 overflow-y-auto space-y-2 pr-1">
            {grouped.length === 0 ? (
              <EmptyState title="No tasks found" body="Try adjusting your search." size="sm" />
            ) : (
              grouped.map(([domain, tasks]) => (
                <div key={domain}>
                  <h3 className="text-caption font-bold text-text-muted uppercase tracking-wider mb-1 mt-2 first:mt-0">
                    {domain} <span className="font-normal">({tasks.length})</span>
                  </h3>
                  <div className="space-y-1">
                    {tasks.map((task) => (
                      <div key={task.id} className="flex items-center gap-2">
                        {!readOnly && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); toggleTaskActive(task.id); }}
                            className={[
                              'w-8 h-5 rounded-full transition-colors shrink-0 relative',
                              task.active ? 'bg-aqua' : 'bg-gray-300',
                            ].join(' ')}
                            aria-label={task.active ? 'Deactivate task' : 'Activate task'}
                          >
                            <span
                              className={[
                                'absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform',
                                task.active ? 'left-3.5' : 'left-0.5',
                              ].join(' ')}
                            />
                          </button>
                        )}
                        <div className="flex-1 min-w-0">
                          <TaskCard
                            task={task}
                            variant="library"
                            selected={selectedTask?.id === task.id}
                            onClick={() => handleTaskClick(task)}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* RIGHT: Task Editor Panel */}
        <div className="flex-1 min-w-0 rounded-lg border border-nn-border bg-nn-white overflow-hidden">
          <TaskEditorPanel
            task={selectedTask}
            onSave={handleSave}
            onRestore={restoreTask}
            readOnly={readOnly}
          />
        </div>
      </div>

      {/* ── Add Task Dialog ── */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Task</DialogTitle>
          </DialogHeader>
          {newTask && (
            <div className="space-y-3 px-6 py-3">
              <div>
                <label className="block text-caption font-bold text-text-muted uppercase tracking-wider mb-1">Task Name</label>
                <input
                  type="text"
                  value={newTask.name}
                  onChange={(e) => updateNewField('name', e.target.value)}
                  placeholder="Enter task name..."
                  className="w-full rounded-md border border-nn-border bg-nn-white px-2.5 py-1.5 text-body-sm focus:outline-none focus:ring-2 focus:ring-aqua/40"
                  autoFocus
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-caption font-bold text-text-muted uppercase tracking-wider mb-1">Domain</label>
                  <NNSelect value={newTask.domain} onChange={(v) => updateNewField('domain', v)} options={DOMAIN_OPTIONS} />
                </div>
                <div>
                  <label className="block text-caption font-bold text-text-muted uppercase tracking-wider mb-1">Cadence</label>
                  <NNSelect value={newTask.cadence} onChange={(v) => updateNewField('cadence', v)} options={CADENCE_OPTIONS} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-caption font-bold text-text-muted uppercase tracking-wider mb-1">Completion Type</label>
                  <NNSelect value={newTask.completionType} onChange={(v) => updateNewField('completionType', v)} options={COMPLETION_OPTIONS} />
                </div>
                <div>
                  <label className="block text-caption font-bold text-text-muted uppercase tracking-wider mb-1">Est. Time (min)</label>
                  <input
                    type="number"
                    value={newTask.estimatedMinutes}
                    onChange={(e) => updateNewField('estimatedMinutes', parseInt(e.target.value) || 0)}
                    className="w-full rounded-md border border-nn-border bg-nn-white px-2.5 py-1.5 text-body-sm focus:outline-none focus:ring-2 focus:ring-aqua/40"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-caption font-bold text-text-muted uppercase tracking-wider mb-1">Owner Role</label>
                  <NNSelect value={newTask.defaultOwnerRole} onChange={(v) => updateNewField('defaultOwnerRole', v)} options={ROLE_OPTIONS} />
                </div>
                <div>
                  <label className="block text-caption font-bold text-text-muted uppercase tracking-wider mb-1">Criticality</label>
                  <NNSelect value={newTask.criticalityTier} onChange={(v) => updateNewField('criticalityTier', v)} options={TIER_OPTIONS} />
                </div>
              </div>
              <div>
                <label className="block text-caption font-bold text-text-muted uppercase tracking-wider mb-1">Tool URL</label>
                <input
                  type="url"
                  value={newTask.toolReference}
                  onChange={(e) => updateNewField('toolReference', e.target.value)}
                  placeholder="https://..."
                  className="w-full rounded-md border border-nn-border bg-nn-white px-2.5 py-1.5 text-body-sm focus:outline-none focus:ring-2 focus:ring-aqua/40"
                />
              </div>
              <div>
                <label className="block text-caption font-bold text-text-muted uppercase tracking-wider mb-1">Description</label>
                <textarea
                  value={newTask.description}
                  onChange={(e) => updateNewField('description', e.target.value)}
                  placeholder="Describe the task..."
                  rows={3}
                  className="w-full rounded-md border border-nn-border bg-nn-white px-2.5 py-1.5 text-body-sm focus:outline-none focus:ring-2 focus:ring-aqua/40 resize-none"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button variant="outline" size="sm" disabled={!newTask?.name?.trim()} onClick={handleAddConfirm}>
              <Check size={ICON_PX.md} className="mr-1" /> Add Task
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default PMTaskLibraryTab;
